import { FLOATING_PANE_CRYPTO_REWARDS_PANE_TYPE, FLOATING_PANE_ORDER_DETAILS_MODE } from 'components/features/rewards/rewardsPane/constants/paneState'
import { CryptoRewardsPane, CryptoRewardsPaneMode, CryptoRewardsPaneState } from 'components/features/rewards/rewardsPane/types/paneState'
import { State } from 'interfaces/state'
import { createSelector, Selector } from 'reselect'
import { createFloatingPaneByTypeSelector } from 'selectors/floatingPanesSelectors'
import { GeneralErrors } from 'interfaces/errors'
import {
  FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE,
  FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE,
  FLOATING_PANE_AUTO_INVESTMENTS_MODE,
} from 'components/features/rewards/rewardsPane/constants/paneState'
import { CryptoRewardsAutoInvestmentsModeSteps } from 'components/features/rewards/rewardsPane/types/paneStateAutoInvestmentsMode'
import { getAutoinvestCurrencyIdOrNullSelector } from 'components/features/rewards/rewardsPane/store/selectors/cryptoRewards'
import { CryptoCurrencyId } from 'components/features/rewards/rewardsPane/types/cryptoCurrency'
import { DEFAULT_CRYPTO_CURRENCY_ID_FOR_INVESTMENT } from 'components/features/rewards/rewardsPane/constants/defaults'
import { CryptoRewardsSellModeSteps } from 'components/features/rewards/rewardsPane/types/paneStateSellMode'

export const floatingPaneStateByPaneTypeSelector = createFloatingPaneByTypeSelector()

export function getCryptoRewardsPaneStateOrNullSelector (storeState: State): CryptoRewardsPane | null {
  return floatingPaneStateByPaneTypeSelector(storeState, FLOATING_PANE_CRYPTO_REWARDS_PANE_TYPE) as CryptoRewardsPane | null
}

export function getCryptoRewardsPaneProgressPercentsStateSelector (storeState: State): number {
  return getCryptoRewardsPaneStateOrNullSelector(storeState)?.progressPercents || 0
}

export function getCryptoRewardsPaneProgressLabelStateSelector (storeState: State): string | null {
  return getCryptoRewardsPaneStateOrNullSelector(storeState)?.progressLabel ?? null
}

export function createCryptoRewardsPaneStateForModeSelector<M extends CryptoRewardsPaneMode> (mode: M) {
  return createSelector(
    getCryptoRewardsPaneStateOrNullSelector,
    (cryptoRewardsPaneState: CryptoRewardsPane | null): CryptoRewardsPane | null => {
      if (cryptoRewardsPaneState?.mode !== mode) {
        return null
      }
      return cryptoRewardsPaneState
    }
  )
}

export const getCryptoRewardsPaneStateSellModeOrNullSelector = createCryptoRewardsPaneStateForModeSelector(FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE) as Selector<State, CryptoRewardsSellModeSteps>

export function createCryptoRewardsPaneFieldsStateForModeSelector<M extends CryptoRewardsPaneMode> (mode: M) {
  const selectorCryptoPaneState = createCryptoRewardsPaneStateForModeSelector(mode)
  return createSelector(
    selectorCryptoPaneState,
    (cryptoRewardsPaneState: CryptoRewardsPane | null): Partial<Exclude<CryptoRewardsPaneState<M>['fields'], undefined>> | null => {
      return (cryptoRewardsPaneState?.fields || null) as Partial<Exclude<CryptoRewardsPaneState<M>['fields'], undefined>>
    }
  )
}

export const getCryptoRewardsPaneFieldsStateBuyModeOrNullSelector = createCryptoRewardsPaneFieldsStateForModeSelector(FLOATING_PANE_CRYPTO_REWARDS_BUY_MODE)

export const getCryptoRewardsPaneFieldsStateSellModeOrNullSelector = createCryptoRewardsPaneFieldsStateForModeSelector(FLOATING_PANE_CRYPTO_REWARDS_SELL_MODE)

export const getCryptoRewardsPaneFieldsStateAutoInvestmentsOrNullSelector = createCryptoRewardsPaneFieldsStateForModeSelector(FLOATING_PANE_AUTO_INVESTMENTS_MODE)

export const getCryptoRewardsPaneFieldsStateOrderDetailsOrNullSelector = createCryptoRewardsPaneFieldsStateForModeSelector(FLOATING_PANE_ORDER_DETAILS_MODE)

export function getCryptoRewardsPaneSellModeErrorsOrNullSelector (storeState: State): GeneralErrors | null {
  return getCryptoRewardsPaneFieldsStateSellModeOrNullSelector(storeState)?.errors ?? null
}

export function getCryptoCurrencyIdChosenForAutoinvestments (storeState: State): CryptoCurrencyId {
  const paneFieldsStateOrNull: Partial<CryptoRewardsAutoInvestmentsModeSteps['fields']> | null = getCryptoRewardsPaneFieldsStateAutoInvestmentsOrNullSelector(storeState)
  const cryptoCurrencyIDChosenForInvestmentsCurrentOrNull: CryptoCurrencyId | null = getAutoinvestCurrencyIdOrNullSelector(storeState)
  return paneFieldsStateOrNull?.cryptoCurrencyIDChosenForInvestment ?? cryptoCurrencyIDChosenForInvestmentsCurrentOrNull ?? DEFAULT_CRYPTO_CURRENCY_ID_FOR_INVESTMENT
}
